// src/pages/webhook/WebhookSettingsPage.tsx
// Webhook global settings — delivery defaults, retry policy.
// Label-left settings rows (FormRow), one pinned Save per section since these
// fields are a batch of related delivery-policy values applied atomically.
// No backend endpoint exists yet — Save stays a no-op until one is exposed.
// See docs/ux/14-forms.md.

import { useState } from 'react';
import { observer } from '@react';
import { useWebhook, useI18n } from '@react/hooks';
import { Button, Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter, Alert, ErrorAlert } from '@react/components';
import { FormRow } from '@/components/form';
import { ToggleSwitch } from '@/components/ToggleSwitch';

interface DeliverySettingsForm {
  timeoutSeconds: string;
  rateLimitPerMinute: string;
  maxRetries: string;
  retryIntervalSeconds: string;
  maxRetryIntervalSeconds: string;
  exponentialBackoff: boolean;
}

const INITIAL_SETTINGS: DeliverySettingsForm = {
  timeoutSeconds: '30',
  rateLimitPerMinute: '60',
  maxRetries: '5',
  retryIntervalSeconds: '60',
  maxRetryIntervalSeconds: '3600',
  exponentialBackoff: true,
};

// Clearable numeric text field — no <input type="number">, no steppers.
// Empty stays empty (unset); coercion to a number happens only at save time.
function NumericField({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
}) {
  return (
    <input
      type="text"
      inputMode="numeric"
      value={value}
      onChange={(e) => onChange(e.target.value.replace(/[^0-9]/g, ''))}
      placeholder={placeholder}
      className="w-28 rounded-md border border-input bg-transparent px-2 py-1 text-sm font-mono text-foreground"
    />
  );
}

export const WebhookSettingsPage = observer(() => {
  const store = useWebhook();
  const { t } = useI18n();
  const [saved, setSaved] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [form, setForm] = useState<DeliverySettingsForm>({ ...INITIAL_SETTINGS });

  const dirty = JSON.stringify(form) !== JSON.stringify(INITIAL_SETTINGS);

  const handleChange = (field: keyof DeliverySettingsForm, value: string | boolean) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setSaved(false);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // TODO: Implement settings save via API — no backend RPC exposes this yet.
      // Coerce to numbers only right before the (currently no-op) save call;
      // empty fields stay unset rather than being forced to 0.
      const payload = {
        timeout_seconds: form.timeoutSeconds === '' ? undefined : Number(form.timeoutSeconds),
        rate_limit_per_minute: form.rateLimitPerMinute === '' ? undefined : Number(form.rateLimitPerMinute),
        max_retries: form.maxRetries === '' ? undefined : Number(form.maxRetries),
        retry_interval_seconds: form.retryIntervalSeconds === '' ? undefined : Number(form.retryIntervalSeconds),
        max_retry_interval_seconds:
          form.maxRetryIntervalSeconds === '' ? undefined : Number(form.maxRetryIntervalSeconds),
        exponential_backoff: form.exponentialBackoff,
      };
      void payload;
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* The tab already names this page; the heading stays for screen readers. */}
      <h1 className="sr-only">{t('webhooks.settingsTitle')}</h1>

      {saved && (
        <Alert variant="success" dismissible onDismiss={() => setSaved(false)}>
          {t('webhooks.saveSuccess')}
        </Alert>
      )}

      {store.error != null && <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} />}

      {/* Delivery defaults */}
      <Card>
        <CardHeader>
          <CardTitle>{t('webhooks.deliveryDefaults')}</CardTitle>
          <CardDescription>{t('webhooks.deliveryDefaultsDesc')}</CardDescription>
        </CardHeader>
        <CardContent className="divide-y divide-border">
          <FormRow label={t('webhooks.settingsTimeout')} labelWidth="md">
            <NumericField
              value={form.timeoutSeconds}
              onChange={(v) => handleChange('timeoutSeconds', v)}
              placeholder="30"
            />
          </FormRow>
          <FormRow label={t('webhooks.settingsRateLimit')} labelWidth="md">
            <NumericField
              value={form.rateLimitPerMinute}
              onChange={(v) => handleChange('rateLimitPerMinute', v)}
              placeholder="60"
            />
          </FormRow>
        </CardContent>
      </Card>

      {/* Retry policy */}
      <Card>
        <CardHeader>
          <CardTitle>{t('webhooks.retryPolicy')}</CardTitle>
          <CardDescription>{t('webhooks.retryPolicyDesc')}</CardDescription>
        </CardHeader>
        <CardContent className="divide-y divide-border">
          <FormRow label={t('webhooks.settingsMaxRetries')} labelWidth="md">
            <NumericField
              value={form.maxRetries}
              onChange={(v) => handleChange('maxRetries', v)}
              placeholder="5"
            />
          </FormRow>
          <FormRow label={t('webhooks.settingsRetryInterval')} labelWidth="md">
            <NumericField
              value={form.retryIntervalSeconds}
              onChange={(v) => handleChange('retryIntervalSeconds', v)}
              placeholder="60"
            />
          </FormRow>
          <FormRow label={t('webhooks.settingsMaxRetryInterval')} labelWidth="md">
            <NumericField
              value={form.maxRetryIntervalSeconds}
              onChange={(v) => handleChange('maxRetryIntervalSeconds', v)}
              placeholder="3600"
            />
          </FormRow>
          <div className="py-3">
            <ToggleSwitch
              label={t('webhooks.settingsExponentialBackoff')}
              checked={form.exponentialBackoff}
              onChange={(v) => handleChange('exponentialBackoff', v)}
            />
          </div>
        </CardContent>
        <CardFooter>
          <Button onClick={handleSave} disabled={!dirty || isSaving}>
            {isSaving ? t('common.saving') : t('common.save')}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
});

export default WebhookSettingsPage;
